import { type FormEvent, type JSX, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { AppLink, useNavigator } from '../../discovery/router';
import { routes } from '../../routes';
import { type SessionUser } from '../../session/SessionProvider';
import { type PaymentsConfig, getPaymentsConfig } from '../checkout/checkout-api';
import {
  STRIPE_APPEARANCE,
  type StripeElements,
  type StripeJs,
  loadStripe,
} from '../checkout/payment-element';
import {
  type AnswerInput,
  type ClinicAppointment,
  type ClinicCard,
  type ClinicForm,
  type ClinicTask,
  type ClinicView,
  type DepositPayment,
  getAftercare,
  getAppointment,
  getClinic,
  getClinics,
  getConsent,
  getHistory,
  getMessages,
  getPhotos,
  getQuestionnaire,
  giveHistory,
  giveQuestionnaire,
  payDeposit,
  photoBlob,
  raiseConcern,
  readConsent,
  sendMessage,
} from './clinics-api';
import './account.css';
import './clinics.css';

/**
 * "My clinics" — Just Treated for Clinics on the website, for a signed-in patient (stage A14).
 *
 * `/account/clinics` is one card per clinic that has linked this person as a patient (and has the
 * clinic switched on); `/account/clinics/:org` is that clinic: what to do before a visit (medical
 * history, the consultation's questionnaire, reading the consent text, the deposit), appointments,
 * messages, photographs the clinic released, and aftercare with concerns. Everything is read from the
 * platform; a deposit is confirmed only when the payment provider says so, so PENDING is never shown
 * as confirmed. Nothing clinical is ever put in an email or a text: those only say there is something
 * to read here.
 */

const CLINICS_PATH = `${routes.account.path}/clinics`;
export const clinicsPath = CLINICS_PATH;
const clinicPath = (org: string, view?: string, extra: Record<string, string> = {}) => {
  const query = new URLSearchParams({ ...(view !== undefined ? { view } : {}), ...extra }).toString();
  return `${CLINICS_PATH}/${encodeURIComponent(org)}${query === '' ? '' : `?${query}`}`;
};

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong. Try again.';
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
function day(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
const pounds = (minor: number) => `£${(minor / 100).toFixed(2)}`;

const STATUS_WORDS: Record<ClinicAppointment['status'], string> = {
  HELD: 'Being booked',
  PENDING: 'Waiting for your deposit',
  CONFIRMED: 'Confirmed',
  ATTENDED: 'Attended',
  NO_SHOW: 'Missed',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Released',
};

interface Loaded<T> {
  readonly value?: T;
  readonly error?: string;
  readonly loading: boolean;
  reload(): void;
}

function useLoaded<T>(load: () => Promise<T>, key: string): Loaded<T> {
  const [state, setState] = useState<{ value?: T; error?: string; loading: boolean }>({ loading: true });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    load()
      .then((value) => live && setState({ value, loading: false }))
      .catch((error: unknown) => live && setState({ error: messageOf(error), loading: false }));
    return () => {
      live = false;
    };
  }, [key, tick]);
  return { ...state, reload: () => setTick((t) => t + 1) };
}

function Panel({
  title,
  children,
  label,
}: {
  readonly title?: string;
  readonly children: ReactNode;
  readonly label?: string;
}) {
  return (
    <section className="jt-acc-panel jt-acc-panel--pad jt-cl-panel" aria-label={label ?? title}>
      {title !== undefined ? <h2 className="jt-cl-h2">{title}</h2> : null}
      {children}
    </section>
  );
}

function Failed({ what, loaded }: { readonly what: string; readonly loaded: Loaded<unknown> }): JSX.Element {
  return (
    <div className="jt-acc-panel jt-acc-panel--pad" role="alert">
      <span className="jt-acc-panel__head">We couldn’t load {what}</span>
      <span className="jt-acc-panel__copy">{loaded.error}</span>
      <button type="button" className="jt-cl-btn jt-cl-btn--soft" onClick={loaded.reload}>
        Try again
      </button>
    </div>
  );
}

function Back({ to, children }: { readonly to: string; readonly children: ReactNode }): JSX.Element {
  return (
    <AppLink to={to} className="jt-site-back">
      {children}
    </AppLink>
  );
}

// -- The list -----------------------------------------------------------------------------

function ClinicList(): JSX.Element {
  const clinics = useLoaded(getClinics, 'clinics');
  return (
    <>
      <Back to={routes.account.path}>Account</Back>
      <h1 className="jt-acc-h1">My clinics</h1>
      {clinics.loading && clinics.value === undefined ? (
        <p role="status">Loading your clinics…</p>
      ) : clinics.error !== undefined ? (
        <Failed what="your clinics" loaded={clinics} />
      ) : (clinics.value?.clinics.length ?? 0) === 0 ? (
        <div className="jt-acc-panel jt-acc-panel--empty" role="status">
          <span className="jt-acc-panel__head">No clinics yet</span>
          <span className="jt-acc-panel__copy">
            When a clinic links your Just Treated account to your record, it appears here.
          </span>
        </div>
      ) : (
        <ul className="jt-cl-cards" aria-label="Your clinics">
          {clinics.value?.clinics.map((c) => (
            <ClinicCardView key={c.organisationId} card={c} />
          ))}
        </ul>
      )}
    </>
  );
}

function ClinicCardView({ card }: { readonly card: ClinicCard }): JSX.Element {
  const next = card.nextAppointment;
  return (
    <li className="jt-acc-panel jt-acc-panel--pad jt-cl-card">
      <AppLink
        to={clinicPath(card.organisationId)}
        className="jt-cl-card__link"
        ariaLabel={`Open ${card.name}`}
      >
        <span className="jt-cl-card__name">{card.name}</span>
        {next !== undefined ? (
          <span className="jt-cl-card__next">
            Next: {when(next.startsAt)} · {next.items.map((i) => i.serviceName).join(', ')} ·{' '}
            {STATUS_WORDS[next.status]}
          </span>
        ) : (
          <span className="jt-cl-card__next">No visit booked</span>
        )}
        <span className="jt-cl-card__counts">
          {card.counts.tasksToDo > 0 ? (
            <span className="jt-cl-badge jt-cl-badge--todo">
              {card.counts.tasksToDo} to do before your visit
            </span>
          ) : (
            <span className="jt-cl-badge">Nothing to do</span>
          )}
          <span className="jt-cl-badge">{card.counts.messages} messages</span>
          <span className="jt-cl-badge">{card.counts.photos} photos</span>
          {card.counts.followUpsDue > 0 ? (
            <span className="jt-cl-badge">{card.counts.followUpsDue} aftercare check-ins due</span>
          ) : null}
        </span>
      </AppLink>
    </li>
  );
}

// -- One clinic ---------------------------------------------------------------------------

const TASK_WORDS: Record<ClinicTask['kind'], string> = {
  MEDICAL_HISTORY: 'Your medical history',
  QUESTIONNAIRE: 'Questionnaire',
  CONSENT_PREREAD: 'Read the consent form',
  DEPOSIT: 'Pay your deposit',
};

function taskLink(org: string, task: ClinicTask): string {
  switch (task.kind) {
    case 'MEDICAL_HISTORY':
      return clinicPath(org, 'history');
    case 'QUESTIONNAIRE':
      return clinicPath(org, 'questionnaire', { tid: task.tid ?? '' });
    case 'CONSENT_PREREAD':
      return clinicPath(org, 'consent', { tid: task.tid ?? '' });
    case 'DEPOSIT':
      return clinicPath(org, 'deposit', { appointment: task.appointmentId ?? '' });
  }
}

function Tasks({ org, view }: { readonly org: string; readonly view: ClinicView }): JSX.Element {
  return (
    <Panel title="Before your visit">
      <ul className="jt-cl-list" aria-label="Before your visit">
        {view.tasks.map((t) => (
          <li key={`${t.kind}:${t.tid ?? t.appointmentId ?? ''}`} className="jt-cl-row">
            <span className="jt-cl-row__text">
              <strong>{TASK_WORDS[t.kind]}</strong>
              {t.subject !== undefined ? <span className="jt-cl-muted"> · {t.subject}</span> : null}
              {t.clarificationRequested === true ? (
                <span className="jt-cl-muted"> · your clinic asked you to check your answers</span>
              ) : null}
            </span>
            {t.state === 'DONE' ? (
              <span className="jt-cl-badge jt-cl-badge--done">
                Done{t.doneAt !== undefined ? ` ${day(t.doneAt)}` : ''}
              </span>
            ) : t.state === 'WAITING' ? (
              <span className="jt-cl-badge">Not ready yet</span>
            ) : (
              <AppLink to={taskLink(org, t)} className="jt-cl-btn" ariaLabel={`${TASK_WORDS[t.kind]}: start`}>
                Start
              </AppLink>
            )}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function Appointments({ view }: { readonly view: ClinicView }): JSX.Element {
  return (
    <Panel title="Appointments">
      {view.appointments.length === 0 ? (
        <p className="jt-cl-muted">No appointments yet.</p>
      ) : (
        <ul className="jt-cl-list" aria-label="Appointments">
          {view.appointments.map((a) => (
            <li key={a.id} className="jt-cl-row">
              <span className="jt-cl-row__text">
                <strong>{when(a.startsAt)}</strong>
                <span className="jt-cl-muted">
                  {' '}
                  ·{' '}
                  {a.items
                    .map((i) =>
                      i.areas.length > 0 ? `${i.serviceName} (${i.areas.join(', ')})` : i.serviceName,
                    )
                    .join(', ')}
                </span>
              </span>
              <span
                className={`jt-cl-badge${a.status === 'CONFIRMED' ? ' jt-cl-badge--done' : a.status === 'PENDING' ? ' jt-cl-badge--todo' : ''}`}
              >
                {STATUS_WORDS[a.status]}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function ClinicHome({ org, view }: { readonly org: string; readonly view: ClinicView }): JSX.Element {
  return (
    <>
      <Tasks org={org} view={view} />
      <Appointments view={view} />
      <nav className="jt-cl-more" aria-label="More from your clinic">
        <AppLink to={clinicPath(org, 'messages')} className="jt-acc-panel jt-acc-panel--pad jt-cl-more__item">
          <strong>Messages</strong>
          <span className="jt-cl-muted">{view.counts.messages} in your conversation</span>
        </AppLink>
        <AppLink to={clinicPath(org, 'photos')} className="jt-acc-panel jt-acc-panel--pad jt-cl-more__item">
          <strong>Photos</strong>
          <span className="jt-cl-muted">{view.counts.photos} shared with you</span>
        </AppLink>
        <AppLink
          to={clinicPath(org, 'aftercare')}
          className="jt-acc-panel jt-acc-panel--pad jt-cl-more__item"
        >
          <strong>Aftercare and concerns</strong>
          <span className="jt-cl-muted">{view.counts.followUpsDue} check-ins due</span>
        </AppLink>
      </nav>
    </>
  );
}

// -- Forms: medical history and questionnaire ------------------------------------------------

function QuestionsForm({
  form,
  submit,
  onSubmit,
}: {
  readonly form: ClinicForm;
  readonly submit: string;
  readonly onSubmit: (answers: readonly AnswerInput[]) => Promise<void>;
}): JSX.Element {
  const start = Object.fromEntries((form.given?.answers ?? []).map((a) => [a.questionKey, a]));
  const [answers, setAnswers] = useState<Record<string, { answer: string; detail: string }>>(
    Object.fromEntries(
      form.questionSet.questions.map((q) => [
        q.key,
        { answer: start[q.key]?.answer ?? '', detail: start[q.key]?.detail ?? '' },
      ]),
    ),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const set = (key: string, patch: Partial<{ answer: string; detail: string }>) =>
    setAnswers((all) => ({ ...all, [key]: { ...(all[key] ?? { answer: '', detail: '' }), ...patch } }));
  const missing = form.questionSet.questions.find((q) => {
    const a = answers[q.key];
    if (a === undefined || a.answer.trim() === '') return true;
    return q.kind === 'YES_NO' && a.answer === 'YES' && q.detailWhenYes === true && a.detail.trim() === '';
  });
  const send = async (event: FormEvent) => {
    event.preventDefault();
    if (missing !== undefined) {
      setError(`Answer: ${missing.text}`);
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await onSubmit(
        form.questionSet.questions.map((q) => {
          const a = answers[q.key] ?? { answer: '', detail: '' };
          return {
            questionKey: q.key,
            answer: a.answer.trim(),
            ...(a.detail.trim() !== '' ? { detail: a.detail.trim() } : {}),
          };
        }),
      );
    } catch (failure) {
      setError(messageOf(failure));
      setBusy(false);
    }
  };
  return (
    <form onSubmit={(event) => void send(event)} className="jt-cl-form" noValidate>
      {form.questionSet.questions.map((q) => {
        const a = answers[q.key] ?? { answer: '', detail: '' };
        return (
          <fieldset key={q.key} className="jt-cl-q">
            <legend className="jt-cl-q__text">{q.text}</legend>
            {q.kind === 'YES_NO' ? (
              <div className="jt-cl-q__choices">
                {(['YES', 'NO'] as const).map((choice) => (
                  <label key={choice} className="jt-cl-choice">
                    <input
                      type="radio"
                      name={q.key}
                      value={choice}
                      checked={a.answer === choice}
                      onChange={() => set(q.key, { answer: choice })}
                    />
                    {choice === 'YES' ? 'Yes' : 'No'}
                  </label>
                ))}
              </div>
            ) : (
              <textarea
                className="jt-cl-input"
                aria-label={q.text}
                rows={2}
                value={a.answer}
                onChange={(event) => set(q.key, { answer: event.target.value })}
              />
            )}
            {q.kind === 'YES_NO' && a.answer === 'YES' && q.detailWhenYes === true ? (
              <label className="jt-cl-label">
                Tell us more
                <textarea
                  className="jt-cl-input"
                  rows={2}
                  value={a.detail}
                  onChange={(event) => set(q.key, { detail: event.target.value })}
                />
              </label>
            ) : null}
          </fieldset>
        );
      })}
      {error !== undefined ? (
        <p className="jt-cl-error" role="alert">
          {error}
        </p>
      ) : null}
      <button type="submit" className="jt-cl-btn" disabled={busy}>
        {busy ? 'Sending…' : submit}
      </button>
    </form>
  );
}

function HistoryView({
  org,
  done,
}: {
  readonly org: string;
  readonly done: (m: string) => void;
}): JSX.Element {
  const form = useLoaded(() => getHistory(org), `history:${org}`);
  if (form.loading && form.value === undefined) return <p role="status">Loading…</p>;
  if (form.value === undefined) return <Failed what="the form" loaded={form} />;
  return (
    <Panel title="Your medical history">
      <p className="jt-cl-muted">
        Your clinic reads this before your visit. Each time you send it, it is kept as a new version: nothing
        you sent before is changed.
        {form.value.given?.version !== undefined
          ? ` You last sent it on ${day(form.value.given.at)} (version ${form.value.given.version}).`
          : ''}
      </p>
      {form.value.clarificationRequested === true ? (
        <p className="jt-cl-note" role="status">
          Your clinic asked you to check your answers. Please read them again and send them.
        </p>
      ) : null}
      <QuestionsForm
        form={form.value}
        submit="Send my medical history"
        onSubmit={async (answers) => {
          await giveHistory(org, answers);
          done('Your medical history was sent to your clinic.');
        }}
      />
    </Panel>
  );
}

function QuestionnaireView({
  org,
  tid,
  done,
}: {
  readonly org: string;
  readonly tid: string;
  readonly done: (m: string) => void;
}): JSX.Element {
  const form = useLoaded(() => getQuestionnaire(org, tid), `questionnaire:${org}:${tid}`);
  if (form.loading && form.value === undefined) return <p role="status">Loading…</p>;
  if (form.value === undefined) return <Failed what="the questionnaire" loaded={form} />;
  return (
    <Panel title={`Questionnaire${form.value.subject !== undefined ? ` · ${form.value.subject}` : ''}`}>
      {form.value.open ? (
        <QuestionsForm
          form={form.value}
          submit="Send my answers"
          onSubmit={async (answers) => {
            await giveQuestionnaire(org, tid, answers);
            done('Your answers were sent to your clinic.');
          }}
        />
      ) : (
        <p role="status">
          {form.value.given !== undefined
            ? `You answered this on ${day(form.value.given.at)}. Your clinic will ask you anything else in person.`
            : 'This questionnaire is closed. Your clinic will ask you in person.'}
        </p>
      )}
    </Panel>
  );
}

function ConsentView({
  org,
  tid,
  done,
}: {
  readonly org: string;
  readonly tid: string;
  readonly done: (m: string) => void;
}): JSX.Element {
  const consent = useLoaded(() => getConsent(org, tid), `consent:${org}:${tid}`);
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  if (consent.loading && consent.value === undefined) return <p role="status">Loading…</p>;
  if (consent.value === undefined) return <Failed what="the consent form" loaded={consent} />;
  const content = consent.value.content;
  return (
    <Panel title={`Read before your visit · ${consent.value.subject}`}>
      {content === undefined ? (
        <p role="status">
          Your clinic has not put its consent form online yet. They will go through it with you.
        </p>
      ) : (
        <>
          <article className="jt-cl-consent" aria-label="Consent form">
            <h3 className="jt-cl-h3">{content.title}</h3>
            <p className="jt-cl-pre">{content.body}</p>
            {content.statements.length > 0 ? (
              <ul className="jt-cl-statements">
                {content.statements.map((s) => (
                  <li key={s.key}>{s.text}</li>
                ))}
              </ul>
            ) : null}
          </article>
          <p className="jt-cl-muted">
            This is not your consent. Your practitioner goes through it with you at the clinic, answers your
            questions, and asks for your consent there. You can change your mind at any time.
          </p>
          {consent.value.read !== undefined ? (
            <p className="jt-cl-badge jt-cl-badge--done" role="status">
              You read this on {day(consent.value.read.at)}
            </p>
          ) : (
            <>
              <label className="jt-cl-choice">
                <input
                  type="checkbox"
                  checked={ticked}
                  onChange={(event) => setTicked(event.target.checked)}
                />
                I have read this consent form
              </label>
              {error !== undefined ? (
                <p className="jt-cl-error" role="alert">
                  {error}
                </p>
              ) : null}
              <button
                type="button"
                className="jt-cl-btn"
                disabled={!ticked || busy}
                onClick={() => {
                  setBusy(true);
                  readConsent(org, tid, content.id)
                    .then(() => done('Thank you. Your clinic can see you have read the consent form.'))
                    .catch((failure: unknown) => {
                      setError(messageOf(failure));
                      setBusy(false);
                    });
                }}
              >
                Confirm I have read it
              </button>
            </>
          )}
        </>
      )}
    </Panel>
  );
}

// -- Deposit ------------------------------------------------------------------------------

function DepositView({
  org,
  appointmentId,
  done,
}: {
  readonly org: string;
  readonly appointmentId: string;
  readonly done: (m: string) => void;
}): JSX.Element {
  const appointment = useLoaded(
    () => getAppointment(org, appointmentId),
    `appointment:${org}:${appointmentId}`,
  );
  const [config, setConfig] = useState<PaymentsConfig | undefined>();
  const [payment, setPayment] = useState<DepositPayment | undefined>();
  const [phase, setPhase] = useState<'idle' | 'starting' | 'card' | 'waiting'>('idle');
  const [error, setError] = useState<string | undefined>();
  const cardHost = useRef<HTMLDivElement | null>(null);
  const stripeRef = useRef<{ stripe: StripeJs; elements: StripeElements } | undefined>(undefined);

  useEffect(() => {
    getPaymentsConfig()
      .then(setConfig)
      .catch(() => setConfig({ available: false, gateway: 'none', testMode: false }));
  }, []);

  const watch = useCallback(async () => {
    setPhase('waiting');
    for (let i = 0; i < 60; i += 1) {
      const now = await getAppointment(org, appointmentId).catch(() => undefined);
      if (now?.status === 'CONFIRMED') {
        done('Your deposit is paid and your appointment is confirmed.');
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
    setPhase('idle');
    setError('We have not heard from the payment provider yet. Check back in a few minutes.');
  }, [org, appointmentId, done]);

  // Stripe: the card form for this deposit's payment.
  useEffect(() => {
    if (phase !== 'card' || payment === undefined || config?.publishableKey === undefined) return;
    const host = cardHost.current;
    if (host === null) return;
    let live = true;
    loadStripe(config.publishableKey)
      .then((stripe) => {
        if (!live) return;
        const elements = stripe.elements({
          clientSecret: payment.clientToken,
          appearance: STRIPE_APPEARANCE,
        });
        elements.create('payment', { layout: 'tabs' }).mount(host);
        stripeRef.current = { stripe, elements };
      })
      .catch(() => live && setError('The secure card form could not load. Reload the page.'));
    return () => {
      live = false;
    };
  }, [phase, payment, config]);

  if (appointment.loading && appointment.value === undefined) return <p role="status">Loading…</p>;
  if (appointment.value === undefined) return <Failed what="the appointment" loaded={appointment} />;
  const a = appointment.value;
  const usesStripe = config?.gateway === 'stripe' && config.publishableKey !== undefined;

  const start = async () => {
    setError(undefined);
    setPhase('starting');
    try {
      const started = await payDeposit(org, appointmentId);
      setPayment(started.payment);
      if (usesStripe) setPhase('card');
      else await watch();
    } catch (failure) {
      setError(messageOf(failure));
      setPhase('idle');
    }
  };
  const confirmCard = async () => {
    const handle = stripeRef.current;
    if (handle === undefined || payment === undefined) return;
    const result = await handle.stripe.confirmPayment({
      elements: handle.elements,
      clientSecret: payment.clientToken,
      redirect: 'if_required',
      confirmParams: { return_url: `${window.location.origin}${clinicPath(org)}` },
    });
    if (result.error !== undefined) {
      setError(result.error.message ?? 'Your payment did not go through. You have not been charged.');
      return;
    }
    await watch();
  };

  return (
    <Panel title="Your deposit">
      <p>
        {when(a.startsAt)} · {a.items.map((i) => i.serviceName).join(', ')}
      </p>
      <p>
        Status: <strong>{STATUS_WORDS[a.status]}</strong>
      </p>
      {a.deposit.status === 'PAID' ? (
        <p className="jt-cl-badge jt-cl-badge--done" role="status">
          Deposit of {pounds(a.deposit.amountMinor)} paid
        </p>
      ) : a.status !== 'PENDING' ? (
        <p role="status">There is no deposit to pay for this appointment now.</p>
      ) : (
        <>
          <p>
            Deposit to hold your appointment: <strong>{pounds(a.deposit.amountMinor)}</strong>. Your
            appointment is confirmed when the payment provider confirms the payment
            {a.expiresAt !== undefined ? `, and is released if it is not paid by ${when(a.expiresAt)}` : ''}.
          </p>
          {config?.gateway === 'fake' ? (
            <p className="jt-cl-note">Test payments only: no card is taken on this site.</p>
          ) : null}
          {phase === 'card' ? (
            <>
              <div ref={cardHost} className="jt-cl-card-el" aria-label="Card details" />
              <button type="button" className="jt-cl-btn" onClick={() => void confirmCard()}>
                Pay {pounds(a.deposit.amountMinor)}
              </button>
            </>
          ) : phase === 'waiting' ? (
            <p role="status">Waiting for the payment provider to confirm your payment…</p>
          ) : (
            <button
              type="button"
              className="jt-cl-btn"
              disabled={phase === 'starting' || config === undefined || config.available === false}
              onClick={() => void start()}
            >
              {config?.available === false
                ? 'Payments are not available'
                : `Pay ${pounds(a.deposit.amountMinor)} deposit`}
            </button>
          )}
        </>
      )}
      {error !== undefined ? (
        <p className="jt-cl-error" role="alert">
          {error}
        </p>
      ) : null}
    </Panel>
  );
}

// -- Messages, photos, aftercare -------------------------------------------------------------

function SafetyLine({ text }: { readonly text: string }): JSX.Element {
  return (
    <p className="jt-cl-safety" role="note">
      {text}
    </p>
  );
}

function MessagesView({ org }: { readonly org: string }): JSX.Element {
  const thread = useLoaded(() => getMessages(org), `messages:${org}`);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  if (thread.loading && thread.value === undefined) return <p role="status">Loading…</p>;
  if (thread.value === undefined) return <Failed what="your messages" loaded={thread} />;
  return (
    <Panel title="Messages">
      <SafetyLine text={`Messages are not for emergencies. ${thread.value.safetyLine}`} />
      {thread.value.messages.length === 0 ? (
        <p className="jt-cl-muted">No messages yet.</p>
      ) : (
        <ol className="jt-cl-thread" aria-label="Conversation">
          {thread.value.messages.map((m) => (
            <li key={m.id} className={`jt-cl-msg jt-cl-msg--${m.from === 'PATIENT' ? 'me' : 'them'}`}>
              <span className="jt-cl-msg__who">
                {m.from === 'PATIENT' ? 'You' : 'Your clinic'} · {when(m.at)}
              </span>
              <span className="jt-cl-pre">{m.body}</span>
            </li>
          ))}
        </ol>
      )}
      <form
        className="jt-cl-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (text.trim() === '') return;
          setBusy(true);
          setError(undefined);
          sendMessage(org, text.trim())
            .then(() => {
              setText('');
              setBusy(false);
              thread.reload();
            })
            .catch((failure: unknown) => {
              setError(messageOf(failure));
              setBusy(false);
            });
        }}
      >
        <label className="jt-cl-label">
          Write to your clinic
          <textarea
            className="jt-cl-input"
            rows={3}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        </label>
        {error !== undefined ? (
          <p className="jt-cl-error" role="alert">
            {error}
          </p>
        ) : null}
        <button type="submit" className="jt-cl-btn" disabled={busy || text.trim() === ''}>
          Send
        </button>
      </form>
    </Panel>
  );
}

function PhotoImage({
  org,
  id,
  label,
}: {
  readonly org: string;
  readonly id: string;
  readonly label: string;
}) {
  const [src, setSrc] = useState<string | undefined>();
  useEffect(() => {
    let url: string | undefined;
    let live = true;
    photoBlob(org, id)
      .then((blob) => {
        if (!live) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => undefined);
    return () => {
      live = false;
      if (url !== undefined) URL.revokeObjectURL(url);
    };
  }, [org, id]);
  return src === undefined ? (
    <span className="jt-cl-photo jt-cl-photo--loading" role="img" aria-label={`${label} (loading)`} />
  ) : (
    <img className="jt-cl-photo" src={src} alt={label} />
  );
}

function PhotosView({ org }: { readonly org: string }): JSX.Element {
  const photos = useLoaded(() => getPhotos(org), `photos:${org}`);
  if (photos.loading && photos.value === undefined) return <p role="status">Loading…</p>;
  if (photos.value === undefined) return <Failed what="your photos" loaded={photos} />;
  return (
    <Panel title="Photos">
      <p className="jt-cl-muted">Only photographs your clinic has shared with you, and any you sent them.</p>
      {photos.value.photos.length === 0 ? (
        <p className="jt-cl-muted">No photos shared yet.</p>
      ) : (
        <ul className="jt-cl-photos" aria-label="Photos">
          {photos.value.photos.map((p) => {
            const label = `${p.stage.replaceAll('_', ' ').toLowerCase()} · ${p.area.replaceAll('_', ' ').toLowerCase()} · ${day(p.releasedAt ?? p.takenAt)}`;
            return (
              <li key={p.id} className="jt-cl-photos__item">
                <PhotoImage org={org} id={p.id} label={label} />
                <span className="jt-cl-muted">{label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

function AftercareView({ org }: { readonly org: string }): JSX.Element {
  const care = useLoaded(() => getAftercare(org), `aftercare:${org}`);
  const [tid, setTid] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  if (care.loading && care.value === undefined) return <p role="status">Loading…</p>;
  if (care.value === undefined) return <Failed what="your aftercare" loaded={care} />;
  const c = care.value;
  return (
    <>
      <Panel title="Aftercare">
        <SafetyLine text={c.safetyLine} />
        {c.followUps.length === 0 ? (
          <p className="jt-cl-muted">No aftercare yet. It appears here after a treatment.</p>
        ) : (
          <ul className="jt-cl-list" aria-label="Aftercare">
            {c.followUps.map((f) => (
              <li key={f.id} className="jt-cl-block">
                <strong>
                  {f.serviceName} · {f.label}
                </strong>
                <span className="jt-cl-muted">
                  {' '}
                  · {f.status === 'DUE' ? `due ${day(f.dueOn)}` : 'complete'}
                </span>
                {f.aftercare !== undefined ? (
                  <details className="jt-cl-details">
                    <summary>{f.aftercare.title}</summary>
                    <p className="jt-cl-pre">{f.aftercare.body}</p>
                  </details>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <Panel title="Concerns">
        {c.concerns.map((x) => (
          <div key={x.id} className="jt-cl-block">
            <strong>{x.serviceName}</strong> · <span className="jt-cl-muted">{day(x.at)}</span>
            <p className="jt-cl-pre">{x.text}</p>
            {x.replies.map((r) => (
              <p key={r.id} className="jt-cl-reply jt-cl-pre">
                {r.body}
              </p>
            ))}
          </div>
        ))}
        {c.treatments.length > 0 ? (
          <form
            className="jt-cl-form"
            onSubmit={(event) => {
              event.preventDefault();
              setBusy(true);
              setError(undefined);
              raiseConcern(org, tid, text.trim())
                .then(() => {
                  setText('');
                  setBusy(false);
                  care.reload();
                })
                .catch((failure: unknown) => {
                  setError(messageOf(failure));
                  setBusy(false);
                });
            }}
          >
            <label className="jt-cl-label">
              Which treatment
              <select className="jt-cl-input" value={tid} onChange={(event) => setTid(event.target.value)}>
                <option value="">Choose…</option>
                {c.treatments.map((t) => (
                  <option key={t.tid} value={t.tid}>
                    {t.serviceName}
                  </option>
                ))}
              </select>
            </label>
            <label className="jt-cl-label">
              Your concern
              <textarea
                className="jt-cl-input"
                rows={3}
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
            </label>
            {error !== undefined ? (
              <p className="jt-cl-error" role="alert">
                {error}
              </p>
            ) : null}
            <button
              type="submit"
              className="jt-cl-btn"
              disabled={busy || tid === '' || text.trim().length < 5}
            >
              Send to my clinic
            </button>
          </form>
        ) : (
          <p className="jt-cl-muted">You can raise a concern here after a treatment.</p>
        )}
      </Panel>
    </>
  );
}

// -- The page -----------------------------------------------------------------------------

function OneClinic({ org }: { readonly org: string }): JSX.Element {
  const navigator = useNavigator();
  const query = navigator.location.query;
  const view = query.get('view') ?? '';
  const clinic = useLoaded(() => getClinic(org), `clinic:${org}:${view}`);
  const [notice, setNotice] = useState<string | undefined>();
  const done = useCallback(
    (message: string) => {
      setNotice(message);
      navigator.navigate(clinicPath(org));
    },
    [navigator, org],
  );
  useEffect(() => {
    if (view !== '') setNotice(undefined);
  }, [view]);

  if (clinic.loading && clinic.value === undefined) return <p role="status">Loading your clinic…</p>;
  if (clinic.value === undefined) {
    return (
      <>
        <Back to={CLINICS_PATH}>My clinics</Back>
        <Failed what="this clinic" loaded={clinic} />
      </>
    );
  }
  const v = clinic.value;
  let body: JSX.Element;
  switch (view) {
    case 'history':
      body = <HistoryView org={org} done={done} />;
      break;
    case 'questionnaire':
      body = <QuestionnaireView org={org} tid={query.get('tid') ?? ''} done={done} />;
      break;
    case 'consent':
      body = <ConsentView org={org} tid={query.get('tid') ?? ''} done={done} />;
      break;
    case 'deposit':
      body = <DepositView org={org} appointmentId={query.get('appointment') ?? ''} done={done} />;
      break;
    case 'messages':
      body = <MessagesView org={org} />;
      break;
    case 'photos':
      body = <PhotosView org={org} />;
      break;
    case 'aftercare':
      body = <AftercareView org={org} />;
      break;
    default:
      body = <ClinicHome org={org} view={v} />;
  }
  return (
    <>
      <Back to={view === '' ? CLINICS_PATH : clinicPath(org)}>{view === '' ? 'My clinics' : v.name}</Back>
      <h1 className="jt-acc-h1">{v.name}</h1>
      {notice !== undefined && view === '' ? (
        <p className="jt-cl-note" role="status">
          {notice}
        </p>
      ) : null}
      {body}
    </>
  );
}

/** `/account/clinics` and `/account/clinics/:org`. */
export function ClinicsPage({ path }: { readonly user: SessionUser; readonly path: string }): JSX.Element {
  const org = /^\/account\/clinics\/([A-Za-z0-9][A-Za-z0-9._:-]{0,99})$/u.exec(path)?.[1];
  return (
    <div className="jt-cl">
      {org === undefined ? <ClinicList /> : <OneClinic org={decodeURIComponent(org)} />}
    </div>
  );
}

/**
 * The "My clinics" entry in the account's navigation: shown only to a person some clinic has linked
 * as a patient, with the clinic switched on. For everyone else (and when the clinic is off) it draws
 * nothing, so the account page is unchanged for them.
 */
export function MyClinicsEntry({ className }: { readonly className: string }): JSX.Element | null {
  const [count, setCount] = useState<number | undefined>();
  useEffect(() => {
    let live = true;
    getClinics()
      .then((found) => live && setCount(found.clinics.length))
      .catch(() => live && setCount(0));
    return () => {
      live = false;
    };
  }, []);
  if (count === undefined || count === 0) return null;
  return (
    <AppLink to={CLINICS_PATH} className={className}>
      <span className="jt-acc-nav__label">My clinics</span>
      <span className="jt-acc-nav__meta">{count}</span>
    </AppLink>
  );
}
